import axios from "axios";
import { useCallback } from "react";
import { nuclearn_api_url } from "src/App";
import { useQuery } from "react-query";
import { NucToast } from "src/reusable/components/Toast";

const getArtifactFile = (artifactId) => {
  return axios
    .get(`${nuclearn_api_url}/reports/run-report`, {
      params: { artifact_id: artifactId },
    })
    .then((response) => {
      return response.data;
    });
};

export const useGetArtifactFile = (artifactId) => {
  return useQuery(
    ["SDRArtifactFile", artifactId],
    () => getArtifactFile(artifactId),
    {
      refetchOnWindowFocus: false,
      enabled: !!artifactId,
    },
  );
};

const getSDRArtifacts = (sdrId) => {
  // Get all artifacts associated with an SDR
  return axios
    .get(`${nuclearn_api_url}/artifacts/`, {
      params: { sdr_id: sdrId },
    })
    .then((response) => {
      return response.data;
    });
};

export const useGetSDRArtifacts = (sdrId) => {
  // Get all artifacts associated with an SDR
  return useQuery(["getSDRArtifacts", sdrId], () => getSDRArtifacts(sdrId), {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!sdrId,
    retry: false,
    staleTime: 1000 * 60 * 30, // 30 minutes - artifact IDs rarely change
    cacheTime: 1000 * 60 * 60, // 1 hour cache retention
    // Suppress 404 errors from being treated as errors
    onError: (error) => {
      if (error?.response?.status !== 404) {
        console.error(`Failed to load artifacts for SDR ${sdrId}:`, error);
      }
    },
  });
};

const getDocumentSDRContent = (sdrId) => {
  // Get all artifacts associated with an SDR
  return axios
    .get(`${nuclearn_api_url}/search/document-content`, {
      params: { sdr_id: sdrId },
    })
    .then((response) => {
      return response.data;
    });
};

export const useGetDocumentSDRContent = (sdrId) => {
  // Get all artifacts associated with an SDR
  return useQuery(
    ["SDRDocumentContent", sdrId],
    () => getDocumentSDRContent(sdrId),
    {
      refetchOnWindowFocus: false,
      enabled: !!sdrId,
    },
  );
};

const getSDRImage = (artifactId) => {
  const url =
    nuclearn_api_url + `/artifacts/${artifactId}/download-artifact-image`;
  // Specify the responseType as 'blob' in the request config
  return axios.get(url, { responseType: "blob" }).then((response) => {
    return response.data; // .data contains the Blob object when responseType is 'blob'
  });
};

export const useGetSDRImage = (sdrId) => {
  // Grabs the SDR image associated with the sdrId.
  // We get the artifact associated with SDR 1 to 1.
  // next call, API will recurse and find the image that was generated from that og artifact id, if there was one.
  const { data: artifacts, isError: artifactsError } =
    useGetSDRArtifacts(sdrId);
  let artifactId = artifacts && artifacts.length > 0 ? artifacts[0]?.id : null;

  return useQuery(["sdrImage", artifactId], () => getSDRImage(artifactId), {
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch when component remounts
    retry: false,
    enabled: !!artifactId && !artifactsError,
    staleTime: 1000 * 60 * 60, // 1 hour - images don't change
    cacheTime: 1000 * 60 * 60 * 2, // 2 hour cache retention
    // Suppress 404 errors from being treated as errors
    onError: (error) => {
      if (error?.response?.status !== 404) {
        console.error(
          `Failed to load image for artifact ${artifactId}:`,
          error,
        );
      }
    },
  });
};

const getDocumentPreview = (artifactid) => {
  console.log(`Getting Document Preview for artifactId: ${artifactid}`);
  if (!artifactid) {
    return null;
  }
  return axios
    .get(`${nuclearn_api_url}/artifacts/${artifactid}/download-document`, {
      responseType: "blob",
    })
    .then(
      (response) => {
        return response.data;
      },
      (error) => {
        console.error("Error getting document preview", error);
      },
    );
};

export const useDownloadDocumentPreview = (artifactid) => {
  return useQuery(
    ["downloadDocumentPreview", artifactid],
    () => getDocumentPreview(artifactid),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: false, // Only fetch on-demand via refetch() when user clicks download
    },
  );
};

const downloadArtifact = (artifactId) => {
  // Downloads the original artifact file with correct content type
  if (!artifactId) {
    return null;
  }
  return axios
    .get(`${nuclearn_api_url}/artifacts/${artifactId}/download`, {
      responseType: "blob",
    })
    .then((response) => {
      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers["content-disposition"];
      let fileName = null;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (match && match[1]) {
          fileName = match[1];
        }
      }
      return { blob: response.data, fileName };
    });
};

export const useDownloadArtifact = (artifactId) => {
  return useQuery(
    ["downloadArtifact", artifactId],
    () => downloadArtifact(artifactId),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: false, // Only fetch on-demand via refetch() when user clicks download
    },
  );
};

/**
 * Fetch an artifact's original file on demand and save it — the blob/anchor/
 * revoke dance shared by every "Download" button that saves an artifact
 * (ProjectFileViewer, InChatDocumentViewer's folder-homed footer).
 */
export const useDownloadArtifactToFile = (artifactId, fallbackFileName) => {
  const { refetch } = useDownloadArtifact(artifactId);
  return useCallback(async () => {
    if (!artifactId) return;
    try {
      const { data } = await refetch();
      if (!data?.blob) return;
      const url = URL.createObjectURL(data.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.fileName || fallbackFileName || "document";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      NucToast({ message: "Download failed", type: "error" });
    }
  }, [artifactId, refetch, fallbackFileName]);
};

const downloadEpicOcrPdf = (artifactId) => {
  if (!artifactId) {
    return null;
  }
  return axios
    .get(`${nuclearn_api_url}/artifacts/${artifactId}/epic-ocr/pdf`, {
      responseType: "blob",
    })
    .then((response) => {
      const contentDisposition = response.headers["content-disposition"];
      let fileName = null;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (match && match[1]) {
          fileName = match[1];
        }
      }
      return { blob: response.data, fileName };
    });
};

export const useDownloadEpicOcrPdf = (artifactId) => {
  return useQuery(
    ["downloadEpicOcrPdf", artifactId],
    () => downloadEpicOcrPdf(artifactId),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: false,
    },
  );
};
